import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { message, Tooltip } from 'antd';
import { LinkBreak, LinkSimple } from '@phosphor-icons/react';

import csrfHeaders from '../../../utils/csrfHeaders';
import UnlinkReferralModal from '../../../components/modal/UnlinkReferralModal';
import {
  hasFeatureInLocalStorage,
  REFERRAL_ENTITY_LINKING_FEATURE,
} from '../../../global/featureFlags';
import './referralLinkAction.scss';

export const ReferralLinkAction = ({
  fromPage, onOpenModal, onUnlinkSuccess, record,
}) => {
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [unlinkSubmitting, setUnlinkSubmitting] = useState(false);

  if (!record) return null;
  if (fromPage !== 'referred') return null;
  if (!hasFeatureInLocalStorage(REFERRAL_ENTITY_LINKING_FEATURE)) return null;

  const isLinkedRecord = Boolean(record.referral_entity_slug) && record.is_primary_referral_record === false;
  if (!isLinkedRecord && !record.has_referral_entity_matches) return null;

  const actionLabel = isLinkedRecord ? 'Unlink from referral' : 'Link to existing referral';
  const Icon = isLinkedRecord ? LinkBreak : LinkSimple;

  const handleLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpenModal();
  };

  const handleUnlinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setUnlinkModalOpen(true);
  };

  const handleUnlinkConfirm = async ({ reason, reason_detail: reasonDetail }) => {
    setUnlinkSubmitting(true);
    try {
      const params = { reason };
      if (reasonDetail) {
        params.reason_detail = reasonDetail;
      }

      await axios.delete(
        `/api/v1/referral_entities/${record.referral_entity_slug}/records/${encodeURIComponent(record.file_token)}`,
        {
          headers: csrfHeaders(),
          params,
        },
      );
    } catch (error) {
      message.error({
        content: error?.response?.data?.error || 'Unable to unlink this referral record. Please try again.',
      });
      setUnlinkSubmitting(false);
      return;
    }

    // DELETE succeeded — surface success and close the modal before refreshing
    // the list. The refresh dispatches through `crud()`, which surfaces HTTP
    // failures by *returning* { error: true } (it does not throw), so we inspect
    // the return value and report a non-blocking warning when the list refetch
    // fails. This avoids showing "Unable to unlink" for a record that was
    // already removed server-side.
    message.success({ content: 'Referral record unlinked' });
    setUnlinkModalOpen(false);
    setUnlinkSubmitting(false);
    const refreshResult = await onUnlinkSuccess();
    if (refreshResult?.error) {
      message.warning({
        content: 'Referral record unlinked, but the list could not be refreshed. Please reload the page.',
      });
    }
  };

  const handleClick = isLinkedRecord ? handleUnlinkClick : handleLinkClick;

  return (
    <>
      <Tooltip title={actionLabel}>
        <button
          aria-label={actionLabel}
          className="referral-link-action"
          onClick={handleClick}
          type="button"
        >
          <Icon size={14} />
        </button>
      </Tooltip>
      {unlinkModalOpen && (
        <UnlinkReferralModal
          hideModal={() => setUnlinkModalOpen(false)}
          isSubmitting={unlinkSubmitting}
          onConfirm={handleUnlinkConfirm}
          recordPatientName={record.patient_name}
        />
      )}
    </>
  );
};

ReferralLinkAction.propTypes = {
  fromPage: PropTypes.string,
  onOpenModal: PropTypes.func.isRequired,
  onUnlinkSuccess: PropTypes.func,
  record: PropTypes.shape({
    file_token: PropTypes.string,
    has_referral_entity_matches: PropTypes.bool,
    is_primary_referral_record: PropTypes.bool,
    patient_name: PropTypes.string,
    referral_entity_slug: PropTypes.string,
  }).isRequired,
};

ReferralLinkAction.defaultProps = {
  fromPage: null,
  onUnlinkSuccess: () => {},
};

export default ReferralLinkAction;
